import React, {FC} from "react";
import {
   GlobalLayout,
   PanelLayout,
   StyledButton,
   StyledDiv,
   StyledTypography
} from "../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {usePublicPageConsumerCreateMutation} from "../../Queries";
import clsx from "clsx";
import {Button, darken, styled, Theme} from "@mui/material";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {AccessContextData} from "../../providers/access/AppContextProvider";
import {LocaleAtom, LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {ReturnsPagePath} from "../restricted/returns/ReturnsPage";
import {useNavigate} from "react-router-dom";
import {CountryMapFactory} from "../../utility/CountryMapFactory";
import {LoginContext} from "../LoginContext";

export const PompDeLuxPage: FC<any> = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles({
      leftImage: localizations.frontPageImage1 || "url('https://storageaccountourimbea2.blob.core.windows.net/brandstatic/left.jpg')",
      rightImage: localizations.frontPageImage2 || "url('https://storageaccountourimbea2.blob.core.windows.net/brandstatic/right.jpg')",
   });

   return <div>
      <StyledDiv type={"main"} className={classes.container}>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.leftImage)}>
            &nbsp;
         </StyledDiv>
         <StyledDiv type={"contrast"} className={classes.main}>
            <div className={classes.content}>
               <StyledTypography variant={"h1"} type={"main"} style={{marginBottom: '26px'}}>
                  {localizations.publicPageTitle}
               </StyledTypography>
               <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '20px'}}>
                  {localizations.publicPageDescription}<br/>
                  {localizations.publicPageDescription2}
               </StyledTypography>
               <div className={classes.accessForm}>
                  <CreateTradeInSessionForm/>
               </div>
            </div>
         </StyledDiv>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.rightImage)}>
            &nbsp;
         </StyledDiv>
      </StyledDiv>
      <PompEasyBox/>
   </div>
}

type imageProps = {
   leftImage: string
   rightImage: string
}

const useStyles = makeStyles({
   backContainer: {
      marginTop: '20px'
   },
   content: {
      maxWidth: '500px',
      textAlign: 'center'
   },
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
      padding: '0 0px 0 0px'
   },
   accessForm: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
   },
   image: {
      width: '400px',
      minHeight: '496px',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "top center",
      overflowX: 'hidden',
      overflowY: 'hidden',
   },
   main: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      flexGrow: 1,
      padding: '50px'
   },
   leftImage: {
      backgroundImage: (props: imageProps) => props.leftImage
   },
   rightImage: {
      backgroundImage: (props: imageProps) => props.rightImage
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
         padding: '0 23px 0 23px'
      } as any,
      image: {
         width: '100%',
         maxHeight: '200px',
         minHeight: '300px',
         backgroundPosition: "center",
      }
   },
   '@media only screen and (max-width: 1024px)': {
      container: {
         padding: '0 23px 0 23px'
      } as any
   },
})

export const CreateTradeInSessionForm = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const appContext = useRecoilValue(AccessContextData);
   const classes = useAccessFormStyles();
   const [createConsumer] = usePublicPageConsumerCreateMutation()
   const navigate = useNavigate();
   const locale = useRecoilValue(LocaleAtom);
   const setLoginContext = useSetRecoilState(LoginContext)

   const onCreate = async () => {
      const countries = CountryMapFactory.getCountriesFromDomain(window.location.hostname)
      const country = countries?.[0]?.alpha2 || undefined

      const {data} = await createConsumer({
         variables: {
            shopId: appContext.shopId,
            locale: locale,
            country: window.Shopify?.country || window.tradeInSession?.countryCode || country,
            currency: appContext.currency || window.Shopify?.currency?.active || window.tradeInSession?.currency
         }
      });
      if (data?.ConsumerCreate?.accessToken) {
         setLoginContext({
            accessToken: data?.ConsumerCreate?.accessToken
         })
         navigate(ReturnsPagePath());
      }
   }

   return <>
      <form className={classes.lightBoxInputArea} onSubmit={async (e) => {
         e.preventDefault();
         await onCreate()
      }}>
         <StyledButton
            className={classes.input}
            id="email"
            variant={"outlined"}
            type={"submit"}
            styletype={"contrast"}
         >
            {localizations.publicPagePageStartButton}
         </StyledButton>
      </form>
   </>
}


const useAccessFormStyles = makeStyles({
   input: {
      width: '100%',
      borderRadius: '0px',
      minWidth: '200px',
      marginBottom: '0px !important',
      border: '1px solid #000000 !important'
   },
   button: {
      height: '45px'
   },
   lightBoxInputArea: {
      display: 'flex',
      alignItems: 'center'
   },
   lightBoxMaxWidth: {
      maxWidth: '60%',
      margin: '0 auto'
   },
   emailSent: {
      marginTop: '20px',
      textAlign: 'center',
      padding: '20px'
   },
   emailSentDivider: {
      marginBottom: '15px'
   },
});


type StyledButtonProps = {
   styletype: 'main' | 'contrast'
}

export const MosMoshButton = styled(Button)<StyledButtonProps>(({theme, variant, styletype}) => {
   const panelLayout = useRecoilValue(PanelLayout(styletype));
   const globalLayout = useRecoilValue(GlobalLayout);

   const button = panelLayout?.mainButton;

   return {
      color: '#56352C' || button?.textColor || undefined,
      '&:hover': {
         color: '#56352C' || button?.textColor || undefined,
         border: button?.border || undefined,
         backgroundColor: 'transparent'
      },
      '&:disabled': {
         color: button?.disabledTextColor || darken(button?.textColor || '#000000', 0.5) + ' !important',
      },
      fontSize: button?.fontSize || globalLayout.text?.fontSize || undefined,
      textTransform: 'none',
      borderRadius: '0',
      fontWeight: 400 || button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
      border: button?.border || undefined,
      textDecoration: 'underline'
   }
});


const PompEasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useEasyStyles();
   return <div className={classes.container}>
      <div>
         <StyledTypography variant={"h1"} className={classes.title}>
            {localizations.easyBoxTitle}
         </StyledTypography>
      </div>
      <div>
         <div className={classes.list}>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://tradein.create2stay.com/lesdeux-choose.jpg"} className={classes.image}
                       alt={localizations.easyBoxSection1Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection1Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection1Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://tradein.create2stay.com/lesdeux-pack.jpg"} className={classes.image}
                       alt={localizations.easyBoxSection2Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection2Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection2Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <img src={"https://tradein.create2stay.com/lesdeux-purchase.jpg"} className={classes.image}
                       alt={localizations.easyBoxSection3Title}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection3Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection3Description}
                  </StyledTypography>
               </div>
            </div>
         </div>
      </div>
   </div>
}


const useEasyStyles = makeStyles((theme: Theme) => ({
      container: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         backgroundColor: '#FFF'
      },
      title: {
         marginTop: '40px'
      },
      text: {
         marginBottom: '15px'
      },
      list: {
         display: 'flex',
         flexDirection: 'row',
         justifyContent: 'center',
         marginTop: '40px'
      },
      listItem: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         marginBottom: '20px',
         gap: '10px'
      },
      listAvatar: {},
      listText: {
         marginTop: '0',
         textAlign: 'center',
         maxWidth: '400px',
         padding: '0 20px 0 20px'
      },
      image: {
         width: '150px'
      },
      '@media only screen and (max-width: 800px)': {
         list: {
            flexDirection: 'column',
         },
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   }),
);
